import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import BaseTable from "@/components_v2/Tables/BaseTable";
import type { CommunityTopic } from "@/redux/certifications/types";
import AnalyticsCard from "@/features/certifications/analytics/components/AnalyticsCard";
import { CARD_SIZES } from "@/features/certifications/analytics/constants";
import { getWeekByModule } from "@/features/certifications/analytics/utils";
import { CommunityCardProps } from "@/features/certifications/analytics/types";

import "../styles.css";

const COUNT_COLUMNS = [
  "posts",
  "comments",
  "accepted",
  "rejected",
  "pending",
  "contributors",
] as const;

const CommunityTopicsCard = ({
  certification,
  data,
  isLoading,
  isError,
}: CommunityCardProps) => {
  const { t } = useTranslation();
  const prefix = "certification.admin.analytics.community";

  const columns = useMemo(() => {
    const weekByModule = getWeekByModule(certification);
    return [
      {
        key: "topic",
        title: t(`${prefix}.columns.topic`),
        render: (_: unknown, row: CommunityTopic) => (
          <span className="flex flex-col">
            <span className="body-s-medium">{row.title}</span>

            <span className="analytics-muted body-xs-regular">
              {t(`${prefix}.moduleLabel`, {
                position: weekByModule.get(row.module.id) ?? "?",
                title: row.module.title,
              })}
            </span>
          </span>
        ),
      },
      ...COUNT_COLUMNS.map((key) => ({
        key,
        dataIndex: key,
        title: t(`${prefix}.columns.${key}`),
        align: "right" as const,
      })),
    ];
  }, [certification, t]);

  return (
    <AnalyticsCard
      title={t(`${prefix}.topicsTitle`)}
      isLoading={isLoading}
      isError={isError}
      size={CARD_SIZES.FULL}
    >
      <BaseTable<CommunityTopic>
        dataSource={data?.topics ?? []}
        columns={columns}
        rowKey="contentItem"
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t(`${prefix}.noTopics`) }}
      />
    </AnalyticsCard>
  );
};

export default CommunityTopicsCard;
